import React from "react";
import { MultiSelect } from "@/components/ui/multi-select";

const SelectionControls = ({
  tenantsLoading,
  tenantsData,
  selectedTenant,
  setSelectedTenant,
  tenantLiteral,
  areaLiteral,
  outletLiteral,
  selectedAreas,
  setSelectedAreas,
  selectedOutlets,
  setSelectedOutlets,
  explicitlyDeselectedOutlets,
  setExplicitlyDeselectedOutlets,
  filtersLoading,
  topicsData,
  selectedTopics,
  setSelectedTopics,
  getAvailableAreas,
  getAvailableOutlets,
}) => {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
      {tenantsLoading ? (
        <div className="flex items-center space-x-3 p-4 bg-white/60 dark:bg-gray-700  rounded-xl border border-modern-light shadow-subtle">
          <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary-600 border-t-transparent"></div>
          <span className="text-sm text-gray-600 dark:text-white font-medium">
            Loading tenants...
          </span>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center space-x-2">
            <MultiSelect
              options={tenantsData.Tenants.map((tenant) => ({
                value: tenant.TenantId.toString(),
                label: tenant.TenantName,
              }))}
              selectedValues={selectedTenant ? [selectedTenant] : []}
              onSelectionChange={(values) =>
                setSelectedTenant(values[0] || "")
              }
              showSelectedValues={(selectedValues) => {
                return `${tenantLiteral}: ${
                  tenantsData.Tenants.find(
                    (tenant) =>
                      tenant.TenantId.toString() === selectedValues[0]
                  )?.TenantName
                }`;
              }}
              placeholder={`Select ${tenantLiteral}`}
              className="w-fit min-w-[200px] dark:text-white dark:bg-gray-700 dark:border-gray-500"
              minSelections={0}
              maxSelections={1}
            />
          </div>
          <div className="flex items-center space-x-2">
            <MultiSelect
              options={getAvailableAreas().map((area) => ({
                value: area.AreaId.toString(),
                label: `${area.AreaCode} - ${area.AreaName}`,
              }))}
              selectedValues={selectedAreas}
              onSelectionChange={(values) => setSelectedAreas(values)}
              placeholder={`Select ${areaLiteral}`}
              className="w-fit min-w-[200px] dark:text-white dark:bg-gray-700 dark:border-gray-500"
              minSelections={0}
              maxSelections={10}
              showSelectedValues={(selectedValues) => {
                const maxAreasToShow = 1;
                const areas = getAvailableAreas().filter((area) =>
                  selectedValues.includes(area.AreaId.toString())
                );
                if (areas.length <= maxAreasToShow) {
                  return `${areaLiteral}: ${areas
                    .map((area) => area.AreaName)
                    .join(", ")}`;
                } else {
                  return `${areaLiteral}: ${areas
                    .slice(0, maxAreasToShow)
                    .map((area) => area.AreaName)
                    .join(", ")} +${areas.length - maxAreasToShow} more`;
                }
              }}
            />
          </div>
          <div className="flex items-center space-x-2">
            <MultiSelect
              options={(() => {
                const outlets = getAvailableOutlets();

                // Map outlets to options with defensive checks
                const mappedOptions = outlets
                  .filter((outlet) => outlet && outlet.OutletId != null)
                  .map((outlet) => {
                    const value = String(outlet.OutletId);
                    const label = outlet.OutletName;
                    return { value, label };
                  });

                // Check for duplicate values
                const values = mappedOptions.map((opt) => opt.value);
                const uniqueValues = new Set(values);
                if (values.length !== uniqueValues.size) {
                  console.error("Duplicate outlet IDs detected!", values);
                }

                return mappedOptions;
              })()}
              selectedValues={selectedOutlets}
              onSelectionChange={(values) => {
                // Track explicitly deselected outlets
                const deselected = selectedOutlets.filter(
                  (id) => !values.includes(id)
                );
                const reselected = values.filter(
                  (id) => !selectedOutlets.includes(id)
                );

                // Add deselected outlets to the explicit deselection list
                if (deselected.length > 0) {
                  setExplicitlyDeselectedOutlets((prev) => [
                    ...new Set([...prev, ...deselected]),
                  ]);
                }

                // Remove re-selected outlets from the explicit deselection list
                if (reselected.length > 0) {
                  setExplicitlyDeselectedOutlets((prev) =>
                    prev.filter((id) => !reselected.includes(id))
                  );
                }

                setSelectedOutlets(values);
              }}
              placeholder={`Select ${outletLiteral}`}
              className="w-fit min-w-[200px] dark:text-white dark:bg-gray-700 dark:border-gray-500"
              minSelections={0}
              maxSelections={100}
              showSelectedValues={(selectedValues) => {
                const maxOutletsToShow = 1;
                const outlets = getAvailableOutlets().filter((outlet) =>
                  selectedValues.includes(String(outlet.OutletId))
                );
                if (outlets.length <= maxOutletsToShow) {
                  return `${outletLiteral}: ${outlets
                    .map((outlet) => outlet.OutletName)
                    .join(", ")}`;
                } else {
                  return `${outletLiteral}: ${outlets
                    .slice(0, maxOutletsToShow)
                    .map((outlet) => outlet.OutletName)
                    .join(", ")} +${outlets.length - maxOutletsToShow} more`;
                }
              }}
            />
          </div>
        </div>
      )}
      {filtersLoading ? (
        <div className="flex items-center space-x-3 p-4 dark:bg-gray-700 bg-white/60 rounded-xl border border-modern-light shadow-subtle">
          <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary-600 border-t-transparent"></div>
          <span className="text-sm text-gray-600 dark:text-white font-medium">
            Loading topics and filters...
          </span>
        </div>
      ) : (
        <div className="flex items-center space-x-2">
          <MultiSelect
            options={topicsData.Topics.map((topic) => ({
              value: topic.Tag,
              label: topic.Label,
            }))}
            selectedValues={selectedTopics}
            onSelectionChange={(values) => {
              setSelectedTopics(values);
            }}
            placeholder="Select topics"
            className="w-fit min-w-[200px] dark:text-white dark:bg-gray-700 dark:border-gray-500"
            minSelections={1}
            showSelectedValues={(selectedValues) => {
              const maxLength = 1;
              if (selectedValues.length === topicsData.Topics.length) {
                return `Topics: All`;
              } else if (selectedValues.length > maxLength) {
                return `Topics: ${selectedValues
                  .slice(0, maxLength)
                  .map(
                    (value) =>
                      topicsData.Topics.find((topic) => topic.Tag === value)
                        ?.Label
                  )
                  .join(", ")}  +  ${selectedValues.length - maxLength} more`;
              } else {
                return `Topics: ${selectedValues
                  .map(
                    (value) =>
                      topicsData.Topics.find((topic) => topic.Tag === value)
                        ?.Label
                  )
                  .join(", ")}`;
              }
            }}
          />
        </div>
      )}
    </div>
  );
};

export default SelectionControls;
